import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FaTimes, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import Select from "react-select";
import {
  priorityOptions,
  getPriorityInfo,
  getStatusBadge,
  getTeamMemberName,
  getAssignedByName,
  getCommenterName,
  priorityTextToNumber,
  statusTextToNumber,
} from "./taskUtils";

const FormatTaskTypeLabel = ({ option }) => (
  <div className="flex items-center gap-2">
    <span>{option.label === "Bug" ? "🐛" : "✅"}</span>
    <span>{option.label}</span>
  </div>
);

FormatTaskTypeLabel.propTypes = {
  option: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
};

export const TaskModal = ({
  showTaskModal,
  modalMode,
  taskFormData,
  usersList,
  loading,
  handleCloseModal,
  handleFormChange,
  handleCreateTask,
  handleUpdateTask,
  taskTypes,
}) => {
  if (!showTaskModal) return null;

  let submitButtonText;
  if (loading) {
    submitButtonText = "Saving...";
  } else if (modalMode === "create") {
    submitButtonText = "Create Task";
  } else {
    submitButtonText = "Update Task";
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {modalMode === "create" ? "Create New Task" : "Edit Task"}
          </h2>
          <button
            onClick={handleCloseModal}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <FaTimes className="text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label
              htmlFor="assignedTo"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Assigned To <span className="text-red-500">*</span>
            </label>
            <Select
              inputId="assignedTo"
              value={taskFormData.assignedTo}
              onChange={(selected) => handleFormChange("assignedTo", selected)}
              options={usersList.map((user) => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name} (${user.email})`,
              }))}
              placeholder="Select a team member"
              isSearchable
              isClearable
            />
          </div>

          <div>
            <label
              htmlFor="taskType"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Task Type <span className="text-red-500">*</span>
            </label>
            <Select
              inputId="taskType"
              value={taskFormData.taskType}
              onChange={(selected) => handleFormChange("taskType", selected)}
              options={taskTypes.map((type) => ({
                value: type.task_type_id,
                label: type.name,
              }))}
              placeholder="Select task type"
              isClearable
              formatOptionLabel={FormatTaskTypeLabel}
            />
          </div>

          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              id="subject"
              type="text"
              value={taskFormData.subject}
              onChange={(e) => handleFormChange("subject", e.target.value)}
              placeholder="Enter task subject"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Description
            </label>
            <textarea
              id="description"
              value={taskFormData.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              placeholder="Enter task description"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-vertical"
            />
          </div>

          <div>
            <label
              htmlFor="priority"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Priority <span className="text-red-500">*</span>
            </label>
            <Select
              inputId="priority"
              value={taskFormData.priority}
              onChange={(selected) => handleFormChange("priority", selected)}
              options={priorityOptions}
              placeholder="Select priority level"
              isClearable
            />
          </div>

          <div>
            <label
              htmlFor="dueDate"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Due Date
            </label>
            <input
              id="dueDate"
              type="date"
              value={taskFormData.dueDate}
              onChange={(e) => handleFormChange("dueDate", e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleCloseModal}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={
              modalMode === "create" ? handleCreateTask : handleUpdateTask
            }
            disabled={
              loading ||
              !taskFormData.assignedTo ||
              !taskFormData.subject ||
              !taskFormData.priority ||
              !taskFormData.taskType // ← ADD
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {submitButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

TaskModal.propTypes = {
  showTaskModal: PropTypes.bool.isRequired,
  modalMode: PropTypes.string.isRequired,
  taskFormData: PropTypes.shape({
    assignedTo: PropTypes.object,
    subject: PropTypes.string,
    description: PropTypes.string,
    priority: PropTypes.object,
    dueDate: PropTypes.string,
    taskType: PropTypes.object,
  }).isRequired,
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  handleCreateTask: PropTypes.func.isRequired,
  handleUpdateTask: PropTypes.func.isRequired,
  taskTypes: PropTypes.arrayOf(
    PropTypes.shape({
      task_type_id: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
};

export const DeleteConfirmModal = ({
  showDeleteConfirm,
  deletingTaskId,
  handleConfirmDelete,
  handleCancelDelete,
}) => {
  if (!showDeleteConfirm) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4 animate-fade-in">
      <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full transform transition-all animate-scale-in">
        <div className="flex items-start gap-3 p-6 border-b border-gray-200 bg-red-50">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <FaTrash className="text-red-600 text-lg" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900">Delete Task</h2>
            <p className="text-sm text-gray-600 mt-1">
              This action cannot be undone
            </p>
          </div>
        </div>

        <div className="p-6">
          <p className="text-gray-700">
            Are you sure you want to delete this task? All associated data
            including comments and sub-tasks will be permanently removed.
          </p>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={handleCancelDelete}
            disabled={deletingTaskId !== null}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            disabled={deletingTaskId !== null}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {deletingTaskId ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Deleting...
              </>
            ) : (
              <>
                <FaTrash className="text-sm" />
                Delete Task
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

DeleteConfirmModal.propTypes = {
  showDeleteConfirm: PropTypes.bool.isRequired,
  deletingTaskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleConfirmDelete: PropTypes.func.isRequired,
  handleCancelDelete: PropTypes.func.isRequired,
};

export const ChildTaskModal = ({
  showChildTaskModal,
  childTaskFormData,
  usersList,
  updatingTask,
  selectedTaskForDetail,
  handleCloseChildTaskModal,
  handleChildTaskFormChange,
  handleCreateChildTask,
}) => {
  if (!showChildTaskModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            Create Child Task
          </h2>
          <button
            onClick={handleCloseChildTaskModal}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close child task modal"
          >
            <FaTimes className="text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Parent Task:</strong> {selectedTaskForDetail.subject} (ID:{" "}
              {selectedTaskForDetail.id})
            </p>
          </div>

          <div>
            <label
              htmlFor="childAssignedTo"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Assigned To <span className="text-red-500">*</span>
            </label>
            <Select
              inputId="childAssignedTo"
              value={childTaskFormData.assignedTo}
              onChange={(selected) =>
                handleChildTaskFormChange("assignedTo", selected)
              }
              options={usersList.map((user) => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name} (${user.email})`,
              }))}
              placeholder="Select a team member"
              isSearchable
              isClearable
            />
          </div>

          <div>
            <label
              htmlFor="childSubject"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              id="childSubject"
              type="text"
              value={childTaskFormData.subject}
              onChange={(e) =>
                handleChildTaskFormChange("subject", e.target.value)
              }
              placeholder="Enter child task subject"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>

          <div>
            <label
              htmlFor="childDescription"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Description
            </label>
            <textarea
              id="childDescription"
              value={childTaskFormData.description}
              onChange={(e) =>
                handleChildTaskFormChange("description", e.target.value)
              }
              placeholder="Enter child task description"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-vertical"
            />
          </div>

          <div>
            <label
              htmlFor="childPriority"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Priority <span className="text-red-500">*</span>
            </label>
            <Select
              inputId="childPriority"
              value={childTaskFormData.priority}
              onChange={(selected) =>
                handleChildTaskFormChange("priority", selected)
              }
              options={priorityOptions}
              placeholder="Select priority level"
              isClearable
            />
          </div>

          <div>
            <label
              htmlFor="childDueDate"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Due Date
            </label>
            <input
              id="childDueDate"
              type="date"
              value={childTaskFormData.dueDate}
              onChange={(e) => {
                if (e.target.value) {
                  handleChildTaskFormChange("dueDate", e.target.value);
                }
              }}
              onClick={(e) => e.target.showPicker?.()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleCloseChildTaskModal}
            disabled={updatingTask}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateChildTask}
            disabled={
              updatingTask ||
              !childTaskFormData.assignedTo ||
              !childTaskFormData.subject ||
              !childTaskFormData.priority
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {updatingTask && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {updatingTask ? "Creating..." : "Create Child Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

ChildTaskModal.propTypes = {
  showChildTaskModal: PropTypes.bool.isRequired,
  childTaskFormData: PropTypes.shape({
    assignedTo: PropTypes.object,
    subject: PropTypes.string,
    description: PropTypes.string,
    priority: PropTypes.object,
    dueDate: PropTypes.string,
  }).isRequired,
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,
  updatingTask: PropTypes.bool.isRequired,
  selectedTaskForDetail: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subject: PropTypes.string,
    description: PropTypes.string,
    priority: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dueDate: PropTypes.string,
    createdOn: PropTypes.string,
    assignedToId: PropTypes.number,
    type: PropTypes.string,
  }).isRequired,
  handleCloseChildTaskModal: PropTypes.func.isRequired,
  handleChildTaskFormChange: PropTypes.func.isRequired,
  handleCreateChildTask: PropTypes.func.isRequired,
};

export const TaskDetailModal = ({
  showTaskDetailModal,
  selectedTaskForDetail,
  usersList,
  isEditingDetail,
  updatingTask,
  editingDescriptionMode,
  editingDescriptionValue,
  comments,
  loadingComments,
  newComment,
  submittingComment,
  childTasks,
  loadingChildTasks,
  handleCloseTaskDetail,
  setIsEditingDetail,
  handleUpdateTaskFromDetail,
  handleStartEditDescription,
  setEditingDescriptionMode,
  setEditingDescriptionValue,
  handleSaveDescription,
  setNewComment,
  handleSubmitComment,
  handleOpenChildTaskModal,
  setSelectedTaskForDetail,
}) => {
  const descriptionRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (editingDescriptionMode && descriptionRef.current) {
      descriptionRef.current.style.height = "auto";
      descriptionRef.current.style.height =
        descriptionRef.current.scrollHeight + "px";
    }
  }, [editingDescriptionMode]);

  if (!showTaskDetailModal || !selectedTaskForDetail) return null;

  const renderCommentsContent = () => {
    if (comments.length === 0) {
      return (
        <div className="text-center py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-sm text-gray-500 mt-3">
            No comments yet. Be the first to comment!
          </p>
        </div>
      );
    }

    return (
      <>
        {comments.map((comment) => (
          <div
            key={comment.id || Math.random()}
            className="bg-gray-50 border border-gray-200 rounded-lg p-3 animate-fade-in"
          >
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                {getCommenterName(comment, usersList).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {getCommenterName(comment, usersList)}
                  </p>
                  <p className="text-xs text-gray-500 flex-shrink-0">
                    {new Date(comment.createdon).toLocaleDateString()}
                  </p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words -mb-1">
                  {comment.comments}
                </p>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  const renderChildTasksContent = () => {
    if (loadingChildTasks) {
      return (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">
            Loading sub-tasks...
          </span>
        </div>
      );
    }

    if (childTasks.length === 0) {
      return (
        <div className="text-center py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-sm text-gray-500 mt-3">No sub-tasks yet.</p>
        </div>
      );
    }

    return (
      <>
        {childTasks.map((childTask) => (
          <div
            key={childTask.id}
            className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {childTask.subject}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-600">
                  <span>{childTask.id}</span>
                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                  <span>{getTeamMemberName(childTask, usersList)}</span>
                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                  <span>
                    Due: {new Date(childTask.dueDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                    getPriorityInfo(childTask.priority).color
                  }`}
                >
                  {getPriorityInfo(childTask.priority).text}
                </span>
                {getStatusBadge(childTask.status)}
              </div>
            </div>
            {childTask.description && (
              <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded border-l-2 border-green-400 line-clamp-2">
                {childTask.description}
              </p>
            )}
          </div>
        ))}
      </>
    );
  };

  // FIXED: Get the correct priority value for the select
  const currentPriorityValue = priorityTextToNumber(
    selectedTaskForDetail.priority,
  );
  const currentPriorityOption = priorityOptions.find(
    (p) => p.value === currentPriorityValue,
  );

  // FIXED: Get the correct status value
  const currentStatusValue = statusTextToNumber(selectedTaskForDetail.status);

  // FIXED: Check if user exists
  const hasAssignedUser = usersList.some(
    (u) => u.id === selectedTaskForDetail.assignedToId,
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 truncate">
              {selectedTaskForDetail.subject}
            </h2>

            <div className="flex items-center flex-wrap gap-3 text-xs text-gray-600 mt-1">
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span>
                Created:{" "}
                {new Date(selectedTaskForDetail.createdOn).toLocaleDateString()}
              </span>
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span className="flex items-center gap-1">
                <span className="uppercase tracking-wide font-semibold text-gray-600">
                  Assigned By:
                </span>
                <span className="text-gray-900 font-medium mb-0">
                  {getAssignedByName(selectedTaskForDetail, usersList)}
                </span>
              </span>

              {selectedTaskForDetail.type && (
                <>
                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                      selectedTaskForDetail.type === "Bug"
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}
                  >
                    {selectedTaskForDetail.type === "Bug" ? "🐛" : "✅"}{" "}
                    {selectedTaskForDetail.type}
                  </span>
                </>
              )}
            </div>
          </div>

          <button
            onClick={handleCloseTaskDetail}
            className="p-1.5 rounded-lg hover:bg-white/80 transition-colors ml-3 flex-shrink-0"
            aria-label="Close task detail modal"
          >
            <FaTimes className="text-gray-600 text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
              <div className="flex items-baseline gap-1.5">
                <label
                  htmlFor="detailAssignedTo"
                  className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                >
                  Assigned To:
                </label>
                {isEditingDetail ? (
                  <div className="min-w-[140px]">
                    <Select
                      inputId="detailAssignedTo"
                      value={
                        hasAssignedUser
                          ? {
                              value: selectedTaskForDetail.assignedToId,
                              label: getTeamMemberName(
                                selectedTaskForDetail,
                                usersList,
                              ),
                            }
                          : null
                      }
                      onChange={(selected) => {
                        if (selected) {
                          handleUpdateTaskFromDetail(
                            "assignedTo",
                            selected.value,
                          );
                        }
                      }}
                      options={usersList.map((user) => ({
                        value: user.id,
                        label: `${user.first_name} ${user.last_name}`,
                      }))}
                      placeholder="Select user"
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                      menuPlacement="auto"
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "28px",
                          fontSize: "13px",
                          borderColor: "#d1d5db",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      isDisabled={updatingTask}
                    />
                  </div>
                ) : (
                  <p className="text-[13px] text-gray-900 font-medium mb-0">
                    {getTeamMemberName(selectedTaskForDetail, usersList)}
                  </p>
                )}
              </div>

              <div className="flex items-baseline gap-1.5">
                <label
                  htmlFor="detailPriority"
                  className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                >
                  Priority:
                </label>
                {isEditingDetail ? (
                  <div className="min-w-[120px]">
                    <Select
                      inputId="detailPriority"
                      value={currentPriorityOption}
                      onChange={(selected) => {
                        if (selected) {
                          handleUpdateTaskFromDetail(
                            "priority",
                            selected.label,
                          );
                        }
                      }}
                      options={priorityOptions}
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                      menuPlacement="auto"
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "28px",
                          fontSize: "13px",
                          borderColor: "#d1d5db",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      isDisabled={updatingTask}
                    />
                  </div>
                ) : (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                      getPriorityInfo(selectedTaskForDetail.priority).color
                    }`}
                  >
                    {getPriorityInfo(selectedTaskForDetail.priority).text}
                  </span>
                )}
              </div>

              <div className="flex items-baseline gap-1.5">
                <label
                  htmlFor="detailStatus"
                  className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                >
                  Status:
                </label>
                {isEditingDetail ? (
                  <select
                    id="detailStatus"
                    value={currentStatusValue}
                    onChange={(e) =>
                      handleUpdateTaskFromDetail(
                        "status",
                        Number(e.target.value),
                      )
                    }
                    className="px-2.5 py-1 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    disabled={updatingTask}
                  >
                    <option value="1">New</option>
                    <option value="2">In Progress</option>
                    <option value="3">Closed</option>
                  </select>
                ) : (
                  getStatusBadge(selectedTaskForDetail.status)
                )}
              </div>

              <div className="flex items-baseline gap-1.5">
                <label
                  htmlFor="detailDueDate"
                  className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                >
                  Due Date:
                </label>
                {isEditingDetail ? (
                  <input
                    id="detailDueDate"
                    type="date"
                    value={
                      selectedTaskForDetail.dueDate
                        ? new Date(selectedTaskForDetail.dueDate)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      handleUpdateTaskFromDetail("dueDate", e.target.value);
                    }}
                    onClick={(e) => e.target.showPicker?.()}
                    className="px-2.5 py-1 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    disabled={updatingTask}
                  />
                ) : (
                  <p className="text-[13px] text-gray-900 font-medium mb-0">
                    {new Date(
                      selectedTaskForDetail.dueDate,
                    ).toLocaleDateString()}
                  </p>
                )}
              </div>

              {!isEditingDetail && (
                <button
                  onClick={() => setIsEditingDetail(true)}
                  className="text-gray-500 hover:text-blue-600 transition-colors ml-auto"
                  aria-label="Edit task details"
                >
                  <FaEdit className="text-sm" />
                </button>
              )}
            </div>

            {isEditingDetail && (
              <div className="flex justify-end gap-2 pt-2.5 mt-2.5 border-t">
                <button
                  onClick={() => setIsEditingDetail(false)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={updatingTask}
                >
                  Done Editing
                </button>
              </div>
            )}

            {updatingTask && (
              <div className="flex items-center justify-center py-2 bg-blue-50 rounded-lg mt-2.5">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-xs font-medium text-blue-600">
                  Updating...
                </span>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-1 h-4 bg-blue-600 rounded" />
              <span>Description</span>
            </h3>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              {editingDescriptionMode ? (
                <div className="space-y-2">
                  <textarea
                    ref={descriptionRef}
                    value={editingDescriptionValue}
                    onChange={(e) => {
                      setEditingDescriptionValue(e.target.value);
                      if (descriptionRef.current) {
                        descriptionRef.current.style.height = "auto";
                        descriptionRef.current.style.height =
                          descriptionRef.current.scrollHeight + "px";
                      }
                    }}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm resize-none overflow-hidden"
                    disabled={updatingTask}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingDescriptionMode(false)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                      disabled={updatingTask}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveDescription}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                      disabled={updatingTask}
                    >
                      {updatingTask && (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      )}
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="cursor-pointer hover:text-blue-600 flex items-start gap-2 group w-full text-left"
                  onClick={handleStartEditDescription}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleStartEditDescription();
                    }
                  }}
                >
                  <p className="text-sm text-gray-700 whitespace-pre-wrap flex-1 mb-0">
                    {selectedTaskForDetail.description ||
                      "No description provided"}
                  </p>
                  <FaEdit className="text-xs text-gray-400 group-hover:text-blue-600 mt-0.5 flex-shrink-0" />
                </button>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-1 h-4 bg-blue-600 rounded"></span>
              Comments ({comments.length})
            </h3>

            <div className="space-y-2.5 mb-3 max-h-48 overflow-y-auto">
              {loadingComments ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600">
                    Loading comments...
                  </span>
                </div>
              ) : (
                renderCommentsContent()
              )}
            </div>

            <div className="border-t pt-3 mt-3 flex flex-wrap items-end gap-3">
              <div className="flex flex-1 items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={newComment}
                  onChange={(e) => {
                    setNewComment(e.target.value);
                    if (textareaRef.current) {
                      textareaRef.current.style.height = "auto";
                      textareaRef.current.style.height =
                        textareaRef.current.scrollHeight + "px";
                    }
                  }}
                  placeholder="Write a comment..."
                  rows={1}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-none text-sm overflow-hidden"
                  disabled={submittingComment}
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={submittingComment || !newComment.trim()}
                  className="px-4 py-2 whitespace-nowrap bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium flex items-center justify-center gap-2"
                >
                  {submittingComment ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                      Posting...
                    </>
                  ) : (
                    "Post"
                  )}
                </button>
              </div>

              <button
                onClick={handleOpenChildTaskModal}
                className="px-4 py-2 whitespace-nowrap bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-medium flex items-center justify-center gap-2"
              >
                <FaPlus className="text-xs" />
                Add Sub-Task
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-1 h-4 bg-green-600 rounded"></span>
              Sub-Tasks ({childTasks.length})
            </h3>

            <div className="space-y-2">{renderChildTasksContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

TaskDetailModal.propTypes = {
  showTaskDetailModal: PropTypes.bool.isRequired,
  selectedTaskForDetail: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subject: PropTypes.string,
    description: PropTypes.string,
    priority: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dueDate: PropTypes.string,
    createdOn: PropTypes.string,
    assignedToId: PropTypes.number,
    type: PropTypes.string,
  }),
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,
  isEditingDetail: PropTypes.bool.isRequired,
  updatingTask: PropTypes.bool.isRequired,
  editingDescriptionMode: PropTypes.bool.isRequired,
  editingDescriptionValue: PropTypes.string.isRequired,
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      comments: PropTypes.string,
      createdon: PropTypes.string,
    }),
  ).isRequired,
  loadingComments: PropTypes.bool.isRequired,
  newComment: PropTypes.string.isRequired,
  submittingComment: PropTypes.bool.isRequired,
  childTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      subject: PropTypes.string,
      description: PropTypes.string,
      priority: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      dueDate: PropTypes.string,
    }),
  ).isRequired,
  loadingChildTasks: PropTypes.bool.isRequired,
  handleCloseTaskDetail: PropTypes.func.isRequired,
  setIsEditingDetail: PropTypes.func.isRequired,
  handleUpdateTaskFromDetail: PropTypes.func.isRequired,
  handleStartEditDescription: PropTypes.func.isRequired,
  setEditingDescriptionMode: PropTypes.func.isRequired,
  setEditingDescriptionValue: PropTypes.func.isRequired,
  handleSaveDescription: PropTypes.func.isRequired,
  setNewComment: PropTypes.func.isRequired,
  handleSubmitComment: PropTypes.func.isRequired,
  handleOpenChildTaskModal: PropTypes.func.isRequired,
  setSelectedTaskForDetail: PropTypes.func.isRequired,
};
